import { createSlice } from '@reduxjs/toolkit';
import { signinApi, verifyToken } from '../api/signin';
import { oauthSigninApi } from '../api/oauth';
import { gstIntegrationApi, sendOtpApi, verifyOtpApi } from '../api/signup';

interface UsersState {
  loading: boolean;
  currentRequestId: undefined | string;
  token: string;
  userData: any;
  location: any;
  userName: any;
  error: any;
}

const initialState = {
  loading: false,
  currentRequestId: undefined,
  token: '',
  userData: null,
  location: null,
  error: null,
} as UsersState;

export const signinSlice = createSlice({
  name: 'signin',
  initialState,
  reducers: {
    localSignin: (state, action) => {
      state.userData = action.payload;
    },
    resetSignin: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase("RESET_ALL", (state, action) => {
        return initialState;
      })
      .addCase(signinApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(signinApi.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.currentRequestId = undefined;
          if (action?.payload?.statusCode !== 200) {
            state.loading = false;
            state.token = '';
            state.userData = null;
            state.location = null;
            state.userName = null;
            state.error = action.payload?.error;
          } else {
            state.loading = false;
            state.token = action.payload?.data?.authorizationToken;
            state.userData = action.payload?.data?.party;
            state.location = action.payload?.data?.location;
            state.userName = action.payload?.data?.userName;
            state.error = null;
          }
        }
      })
      .addCase(signinApi.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.error = action.error?.message || 'Login failed';
        }
      })
      .addCase(oauthSigninApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(oauthSigninApi.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.currentRequestId = undefined;
          state.loading = false;

          // If registration is required, don't set authentication state
          if (action.payload?.data?.requiresRegistration) {
            state.token = '';
            state.userData = null;
            state.location = null;
            state.userName = null;
            state.error = null;
          } else {
            // Normal successful authentication
            state.token = action.payload?.data?.authorizationToken;
            state.userData = action.payload?.data?.party;
            state.location = action.payload?.data?.location;
            state.userName = action.payload?.data?.userName;
            state.error = null;
          }
        }
      })
      .addCase(oauthSigninApi.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          const payload = action.payload as any;
          state.error = payload?.error || action.error.message;
        }
      })
      .addCase(verifyToken.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        if (state.loading) {
          state.loading = false;
        }
      })
      .addCase(verifyToken.rejected, (state, action) => {
        if (state.loading) {
          state.loading = false;
        }
      })
      .addCase(sendOtpApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId
      })
      .addCase(sendOtpApi.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.userData = action.payload?.data;
        }
      })
      .addCase(sendOtpApi.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.error = action.error?.message || 'Something went wrong';
        }
      })
      .addCase(verifyOtpApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId
      })
      .addCase(verifyOtpApi.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.currentRequestId = undefined;
          if (action?.payload?.status !== 200) {
            state.loading = false;
            state.token = '';
            state.userData = null;
            state.location = null;
            state.userName = null;
            state.error = action.payload?.error;
          } else {
            state.loading = false;
            state.token = action.payload?.data?.authorizationToken;
            state.userData = action.payload?.data?.party;
            state.location = action.payload?.data?.location;
            state.userName = action.payload?.data?.userName;
            state.error = null;
          }
        }
      })
      .addCase(verifyOtpApi.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.error = action.error?.message || 'Something went wrong';
        }
      })
      .addCase(gstIntegrationApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId
      })
      .addCase(gstIntegrationApi.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.userData = action.payload?.data;
        }
      })
      .addCase(gstIntegrationApi.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          state.currentRequestId = undefined;
          state.error = action.error?.message || 'Something went wrong';
        }
      });
  },
});

export const { localSignin, resetSignin } = signinSlice.actions;

export default signinSlice.reducer;
